import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Theme } from '@mui/material/styles';
import { orderBy } from '@progress/kendo-data-query';
import { makeStyles } from 'tss-react/mui';
import { Accordion, AccordionDetails, AccordionSummary } from '../components/core/expansion-panel';
import { IUser } from 'contexts/user-context';
import { useBoolean, useLocalState, useTranslation } from 'hooks';
// eslint-disable-next-line max-len
import { OrganisationRoleFields_OrganisationRole_rights_OrganisationRoleRight as OrganisationRoleRightFields } from 'queries/generated/fragments.generated';
import { GET_STUDY_SUMMARIES } from 'queries/studies';
import { getIsActiveField } from 'utils/convertor';
import Loading from 'views/components/core/loading';
import HomeRiskAssessmentSummary from 'views/components/home/ra-summary';
import HomeStudySummary from 'views/components/home/study-summary';
import HomeWelcome from 'views/components/home/welcome';
import './home.scss';
import {
    getStudySummaries,
    getStudySummaries_OpraQueries_studySummaries_StudySummary,
} from '@queries/generated/studies.generated';

// makeStyles is deprecated in MUI v5, but still works until later upgrades
// It leads to errors below of the type "Property 'foo' does not exist on type 'ClassNameMap<never>'"
// (because createStyles now returns never to mark it as deprecated)
// With MUI v5, we should be using the new styled API or the sx prop instead.
// This may be required if we ever need to upgrade again, but hopefully we can avoid this
// (We only needed to upgrade in 2026.1 because React 19 required an upgrade to Mui v5)
// https://mui.com/material-ui/migration/migrating-from-jss/
const useStyles = makeStyles()((theme: Theme) => ({
    tilesWrapper: {
        display: 'flex',
        width: '100%',
        alignItems: 'stretch',
        paddingBottom: 30,
        [theme.breakpoints.down(1350)]: {
            flexDirection: 'column',
            paddingBottom: 0,
        },
    },
    firstTileItem: {
        flex: 1,
    },
    noStudy: {
        marginRight: 0,
    },
    homeWrapper: {
        width: '100%',
        textAlign: 'center',
        padding: 20,
    },
    complianceReport: {
        marginLeft: '15px',
    },
}));

interface StudySummaryWithSortOrder
    extends Partial<Omit<getStudySummaries_OpraQueries_studySummaries_StudySummary, '__typename'>> {
    _sortOrdering: number;
}

const Home = ({ user }: { user: IUser }) => {
    const { t } = useTranslation();
    const { classes } = useStyles();

    const [loadData, { data, error, loading }] = useLazyQuery<getStudySummaries>(GET_STUDY_SUMMARIES);
    const { state } = useLocalState();
    const [studySummaries, setStudySummaries] = useState<StudySummaryWithSortOrder[]>([]);
    const expanded = useBoolean(false);
    const populateData = () => {
        if (!expanded.value && studySummaries.length === 0) {
            loadData();
        }
        expanded.setValue(!expanded.value);
    };

    useEffect(() => {
        if (data?.studySummaries && state?.userStudies && user.organisation) {
            const array = data.studySummaries.map((item) => {
                const isDefault =
                    user.organisation.members?.find((el) => el?.userId === user.id)?.defaultStudyId === item?.studyId;
                const userStudy = state.userStudies?.find((el) => el?.id === item?.studyId);
                const isFavourite = userStudy?.members?.find((el) => el?.userId === user.id)?.favourite;
                return {
                    ...item,
                    _sortOrdering: isDefault ? 1 : isFavourite ? 2 : 3,
                };
            });
            setStudySummaries(
                orderBy(array, [
                    { field: '_sortOrdering', dir: 'asc' },
                    { field: 'studyNum', dir: 'asc' },
                ]),
            );
        }
    }, [data, state?.userStudies, user.organisation]);

    if (!user || !state?.userStudies) return <Loading float />;

    const hasStudies = state?.userStudies?.length > 0;

    const isShowSummary = (module: string, moduleId: number) => {
        const isActiveUser = state?.selectedStudy?.members?.find((item) => item?.userId === user.id)?.active ?? false;
        const isValidPermissions =
            user?.role?.rights.find(
                (item: OrganisationRoleRightFields) =>
                    item.moduleId === moduleId && item.element?.match(/page/) && item.permissions > 0,
            ) ?? false;
        return isActiveUser && getIsActiveField(module, state?.selectedStudy?.modules) && isValidPermissions;
    };

    // An error can occur in the GET_STUDY_SUMMARIES query if we're an admin here without an organisation
    if (error) console.error('Error getting study summaries: ', error);
    return (
        <div className={classes.homeWrapper}>
            {loading && <Loading float />}
            <div className={classes.tilesWrapper}>
                <div className={`${classes.firstTileItem} ${!hasStudies ? classes.noStudy : ''}`}>
                    <HomeWelcome user={user} noStudies={!hasStudies} />
                </div>
            </div>
            {(isShowSummary('RAM', 2) || isShowSummary('CM', 1)) && (
                <Accordion
                    className="home-display-charts-panel"
                    expanded={expanded.value}
                    slotProps={{ heading: { component: 'div' } }}
                >
                    <div
                        className="risk-detail-item-wrapper home-display-charts-panel-header"
                        onClick={() => {
                            populateData();
                        }}
                    >
                        <div className="header dark-title">
                            <AccordionSummary className="home-display-charts-summary">
                                <div className="title">{t('uam:displayCharts')}</div>
                                <div className="pull-right">
                                    <FontAwesomeIcon
                                        id="risk-details-chevron-button"
                                        icon={['fal', expanded.value ? 'chevron-up' : 'chevron-down']}
                                        className="fa-lg arrow-btn pull-right"
                                    />
                                </div>
                            </AccordionSummary>
                        </div>
                    </div>
                    <AccordionDetails className="panel-details-table">
                        {studySummaries.length > 0 && (
                            <>
                                {isShowSummary('RAM', 2) && (
                                    <HomeRiskAssessmentSummary
                                        summaries={studySummaries}
                                        userStudies={state.userStudies}
                                    />
                                )}
                                {studySummaries.map((summary) => (
                                    <HomeStudySummary
                                        key={summary.studyNum}
                                        summary={summary}
                                        studyInfo={state?.userStudies?.find((item) => item?.id === summary?.studyId)}
                                        user={user}
                                    />
                                ))}
                            </>
                        )}
                    </AccordionDetails>
                </Accordion>
            )}
        </div>
    );
};

export default Home;
